import { useEffect, useState } from "react";

import {
  ListContainer,
  ListContext,
  PAGE_DEFAULT,
  PAGE_SIZE_UNLIMITED,
  Page,
  PageRoute,
  SearchBar,
  buildPath,
  buildTemplatePath,
  pn,
} from "@compass/core";

import { ListFooter } from "components";
import { DistrictListDto, DistrictListResource, FileCategoryListDto, ListFilterBase, Permission } from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { FileCategoryListPageHeader } from "./FileCategoryListPageHeader";
import { FileCategoryTable } from "./FileCategoryTable";

const routeName = "fileCategories";

export const fileCategoryListPageRoute: PageRoute = {
  handle: "File Categories",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const FileCategoryListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.MatchUtility;

  const [listItems, setListItems] = useState<FileCategoryListDto[]>([]);
  const { listFilter, setListFilter } = useInitListFilter<ListFilterBase>({
    sortObjects: [{ property: pn<FileCategoryListDto>("name"), isDesc: false }],
  });
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<DistrictListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.fileCategories.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.fileCategories.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);

    ApiService.fileCategories.getListResource().then((response) => {
      setResource(response);
    });
  }, []);

  return (
    <>
      <Page header={<FileCategoryListPageHeader rootPermission={ROOT_PERMISSION} handle={fileCategoryListPageRoute.handle} />}>
        <ListContainer
          fullHeight
          header={<SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />}
          tableContainer={
            <FileCategoryTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              fileCategories={listItems}
              setFileCategories={setListItems}
              resource={resource}
              searchFocused={searchFocused}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
